/** 3ᵉ bloc fiche : devis / factures + PDF envoyés depuis le Coffre. */
import { useEffect, useState } from "react";
import { facturxDownload, facturxFetch } from "../../api/client";
import { ConfirmModal } from "../../components/ConfirmModal";
import { useFormatters } from "../../formats/useFormatters";
import { printHtmlUrl } from "../../pdf/printSheet";
import { quotePrintUrl } from "../quotes/quotePdf";
import "./clientDocuments.css";

export type ClientDocItem = {
  id: number;
  kind: "quote" | "invoice" | "credit_note" | "file" | string;
  source: "piece" | "coffre" | string;
  title: string;
  label: string;
  status?: string;
  issued_at: string;
  size_bytes?: number;
  original_name?: string;
};

type ClientDocumentsProps = {
  companyId: number;
  clientId: number;
  onOpenQuote?: (id: number) => void;
  onOpenInvoice?: (id: number) => void;
};

function kindClass(kind: string): string {
  if (kind === "quote") return "is-quote";
  if (kind === "invoice") return "is-invoice";
  if (kind === "file") return "is-file";
  return "";
}

function canDelete(item: ClientDocItem): boolean {
  if (item.source === "coffre" || item.kind === "file") {
    return true;
  }
  return item.source === "piece" && item.status === "draft";
}

export function ClientDocuments({ companyId, clientId, onOpenQuote, onOpenInvoice }: ClientDocumentsProps) {
  const { day } = useFormatters();
  const [items, setItems] = useState<ClientDocItem[]>([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState<ClientDocItem | null>(null);

  function reload() {
    return facturxFetch<{ items: ClientDocItem[] }>(
      `client-files.php?company_id=${companyId}&client_id=${clientId}`
    )
      .then((data) => setItems(data.items || []))
      .catch(() => setErr("Impossible de lire le dossier."));
  }

  useEffect(() => {
    let live = true;
    setErr("");
    facturxFetch<{ items: ClientDocItem[] }>(
      `client-files.php?company_id=${companyId}&client_id=${clientId}`
    )
      .then((data) => {
        if (live) setItems(data.items || []);
      })
      .catch(() => {
        if (live) setErr("Impossible de lire le dossier.");
      });
    return () => {
      live = false;
    };
  }, [companyId, clientId]);

  function open(item: ClientDocItem) {
    if (item.kind === "quote" && onOpenQuote) {
      onOpenQuote(item.id);
      return;
    }
    if ((item.kind === "invoice" || item.kind === "credit_note") && onOpenInvoice) {
      onOpenInvoice(item.id);
      return;
    }
    if (item.kind === "file") {
      void facturxDownload(`client-files.php?action=download&id=${item.id}`, item.original_name || item.title);
      return;
    }
    printHtmlUrl(quotePrintUrl(item.id, true), undefined, item.title);
  }

  async function remove() {
    if (!toDelete) return;
    setBusy(true);
    setErr("");
    try {
      if (toDelete.source === "coffre" || toDelete.kind === "file") {
        await facturxFetch(`client-files.php?id=${toDelete.id}`, { method: "DELETE" });
      } else {
        await facturxFetch(`documents.php?id=${toDelete.id}`, { method: "DELETE" });
      }
      setToDelete(null);
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Suppression impossible.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="fx-fiche-card fx-docs">
      <h2>Documents</h2>
      {err ? <p className="fx-docs__err">{err}</p> : null}
      {items.length === 0 && !err ? (
        <p className="fx-docs__empty">
          Aucun document. Les devis et factures s’y classent seuls ; un PDF peut arriver depuis le
          Coffre. Un brouillon ou un PDF Coffre se retire ici ; une pièce validée, non.
        </p>
      ) : (
        <ul className="fx-docs__list">
          {items.map((item) => (
            <li key={`${item.kind}-${item.id}`} className="fx-docs__item">
              <button type="button" className="fx-docs__row" onClick={() => open(item)}>
                <span className={`fx-docs__kind ${kindClass(item.kind)}`}>{item.label}</span>
                <span className="fx-docs__title">{item.title}</span>
                <span className="fx-docs__date">{day(item.issued_at)}</span>
              </button>
              {canDelete(item) ? (
                <button
                  type="button"
                  className="fx-docs__del"
                  aria-label={`Supprimer ${item.title}`}
                  onClick={() => setToDelete(item)}
                >
                  Retirer
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <ConfirmModal
        open={toDelete !== null}
        title={toDelete?.kind === "file" ? "Retirer ce PDF ?" : "Supprimer ce brouillon ?"}
        message={
          toDelete?.kind === "file"
            ? "Le fichier quitte le dossier client. Le Coffre n’est pas touché."
            : "Seul un brouillon part. Une pièce validée reste figée."
        }
        confirmLabel="Supprimer"
        busy={busy}
        onConfirm={() => void remove()}
        onCancel={() => {
          if (!busy) setToDelete(null);
        }}
      />
    </section>
  );
}
